/**
 * ContactCardView.tsx - Card-based contact display
 * 
 * CHANGE HISTORY:
 * - 251031_1720 [pending] danielbeer: Initial creation for Contactr integration
 * 
 * Universal card view for displaying contacts with avatar, details, and actions.
 * Consumes Contact data from Contactr package.
 */
import React, { useState, useMemo, useCallback } from 'react';
import type { Contact } from '@opcentral/contactr';
import './ContactCardView.css';

export interface ContactCardViewProps {
  /** Contact data to display */
  contacts: Contact[];
  /** Enable selection */
  selectable?: boolean;
  /** Selected contact IDs */
  selectedIds?: string[];
  /** Selection change handler */
  onSelectionChange?: (ids: string[]) => void;
  /** Contact click handler */
  onContactClick?: (contact: Contact) => void;
  /** Contact action handlers */
  onEmail?: (contact: Contact) => void;
  onCall?: (contact: Contact) => void;
  onMessage?: (contact: Contact) => void;
  /** Grid columns (responsive) */
  columns?: 1 | 2 | 3 | 4 | 6;
  /** Show contact groups */
  showGroups?: boolean;
  /** Show contact tags */
  showTags?: boolean;
  /** Custom card renderer */
  renderCard?: (contact: Contact) => React.ReactNode;
}

/**
 * ContactCardView Component
 * 
 * Displays contacts in a responsive card grid with:
 * - Avatar/photo
 * - Name and title
 * - Primary email and phone
 * - Company
 * - Tags and groups
 * - Quick actions (email, call, message)
 */
export function ContactCardView({
  contacts,
  selectable = false,
  selectedIds = [],
  onSelectionChange,
  onContactClick,
  onEmail,
  onCall,
  onMessage,
  columns = 3,
  showGroups = true,
  showTags = true,
  renderCard,
}: ContactCardViewProps) {
  const [selectedSet, setSelectedSet] = useState<Set<string>>(new Set(selectedIds));

  // Update selected set when prop changes
  React.useEffect(() => {
    setSelectedSet(new Set(selectedIds));
  }, [selectedIds]);

  // Handle selection toggle
  const handleToggleSelection = useCallback((contactId: string) => {
    if (!selectable) return;

    const newSet = new Set(selectedSet);
    if (newSet.has(contactId)) {
      newSet.delete(contactId);
    } else {
      newSet.add(contactId);
    }
    setSelectedSet(newSet);

    if (onSelectionChange) {
      onSelectionChange(Array.from(newSet));
    }
  }, [selectable, selectedSet, onSelectionChange]);

  // Handle contact click
  const handleContactClick = useCallback((contact: Contact) => {
    if (onContactClick) {
      onContactClick(contact);
    }
  }, [onContactClick]);

  // Get primary email
  const getPrimaryEmail = (contact: Contact): string | undefined => {
    const primary = contact.emails.find((e) => e.isPrimary);
    return primary?.email || contact.emails[0]?.email;
  };

  // Get primary phone
  const getPrimaryPhone = (contact: Contact): string | undefined => {
    const primary = contact.phones.find((p) => p.isPrimary);
    return primary?.number || contact.phones[0]?.number;
  };

  // Grid class based on columns
  const gridClass = `contact-card-grid contact-card-grid-${columns}`;

  return (
    <div className="contact-card-view">
      <div className={gridClass}>
        {contacts.map((contact) => {
          const isSelected = selectedSet.has(contact.id);
          const primaryEmail = getPrimaryEmail(contact);
          const primaryPhone = getPrimaryPhone(contact);

          // Use custom renderer if provided
          if (renderCard) {
            return (
              <div key={contact.id} className="contact-card-wrapper">
                {renderCard(contact)}
              </div>
            );
          }

          return (
            <div
              key={contact.id}
              className={`contact-card ${isSelected ? 'contact-card-selected' : ''}`}
              onClick={() => handleContactClick(contact)}
            >
              {/* Selection checkbox */}
              {selectable && (
                <div className="contact-card-select">
                  <input
                    type="checkbox"
                    checked={isSelected}
                    onChange={(e) => {
                      e.stopPropagation();
                      handleToggleSelection(contact.id);
                    }}
                    className="contact-card-checkbox"
                  />
                </div>
              )}

              {/* Avatar */}
              <div className="contact-card-avatar">
                {contact.avatarUrl ? (
                  <img src={contact.avatarUrl} alt={contact.displayName} />
                ) : (
                  <div className="contact-card-avatar-placeholder">
                    {contact.displayName.charAt(0).toUpperCase()}
                  </div>
                )}
              </div>

              {/* Name and title */}
              <div className="contact-card-header">
                <h3 className="contact-card-name">{contact.displayName}</h3>
                {contact.jobTitle && (
                  <p className="contact-card-title">{contact.jobTitle}</p>
                )}
                {contact.company && (
                  <p className="contact-card-company">{contact.company}</p>
                )}
              </div>

              {/* Contact methods */}
              <div className="contact-card-details">
                {primaryEmail && (
                  <div className="contact-card-detail">
                    <span className="contact-card-icon">✉️</span>
                    <span className="contact-card-text">{primaryEmail}</span>
                  </div>
                )}
                {primaryPhone && (
                  <div className="contact-card-detail">
                    <span className="contact-card-icon">📞</span>
                    <span className="contact-card-text">{primaryPhone}</span>
                  </div>
                )}
              </div>

              {/* Tags */}
              {showTags && contact.tags.length > 0 && (
                <div className="contact-card-tags">
                  {contact.tags.slice(0, 3).map((tag) => (
                    <span key={tag} className="contact-card-tag">
                      {tag}
                    </span>
                  ))}
                  {contact.tags.length > 3 && (
                    <span className="contact-card-tag contact-card-tag-more">
                      +{contact.tags.length - 3}
                    </span>
                  )}
                </div>
              )}

              {/* Quick actions */}
              <div className="contact-card-actions">
                {onEmail && primaryEmail && (
                  <button
                    className="contact-card-action"
                    onClick={(e) => {
                      e.stopPropagation();
                      onEmail(contact);
                    }}
                    title="Send email"
                  >
                    ✉️
                  </button>
                )}
                {onCall && primaryPhone && (
                  <button
                    className="contact-card-action"
                    onClick={(e) => {
                      e.stopPropagation();
                      onCall(contact);
                    }}
                    title="Call"
                  >
                    📞
                  </button>
                )}
                {onMessage && (
                  <button
                    className="contact-card-action"
                    onClick={(e) => {
                      e.stopPropagation();
                      onMessage(contact);
                    }}
                    title="Send message"
                  >
                    💬
                  </button>
                )}
              </div>
            </div>
          );
        })}
      </div>

      {/* Empty state */}
      {contacts.length === 0 && (
        <div className="contact-card-empty">
          <p>No contacts to display</p>
        </div>
      )}
    </div>
  );
}

export default ContactCardView;
